import React, { useEffect, useState } from 'react';
import { Container, Row, Col } from 'ds/components/grid-system';
import { useLocale } from 'locale';
import ChatActiveIcon from 'assets/svg/chat-active.svg';
import HeartExtraIcon from 'assets/svg/heart-extra.svg';
import LastContactCard from 'components/cards/listing/LastContactCard';
import { CardEventSource, DatedPoi, IBulletin, IProject, PoiId } from 'utils/entities';
import ChevronDown from 'assets/svg/chevron-down.svg';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import EmptyState from 'ds/components/empty-list';
import { SimpleCopyrightFooter } from 'components/footer/SimpleCopyrightFooter';
import { IContactAgentValues } from 'helpers/contactFormStore';
import ChevronUp from 'assets/svg/chevron-up.svg';
import { CardsCarousel } from 'ds/components/carousels/cards-carousel/CardsCarousel';
import { ShortlistPageSkeleton } from 'screens/ShortlistPage/ShortlistPageSkeleton';

import ShortlistCardWithControls from './ShortlistCardWithControls';
import {
  ShortlistPageWrapper,
  ShortlistHeader,
  LastContactEmptyState,
  LastContactSectionContainer,
  ShortlistPageEmptyStateText,
  ShortListSectionWrapper,
  ShowMore,
  FooterWrapper,
  EmptyStateWrapper,
} from './styled';

import { SubsectionHeader } from 'components/feed-sections-utility';
import { ScrollBox } from 'ds/components/carousels/scroll-box';
import { NavigateToFn } from 'config/routes';

interface ShortlistPageProps {
  lastContactedList: DatedPoi[];
  savedListingList: DatedPoi[];
  lastViewedIds: PoiId[];
  isLoading: boolean;
  contactFormInitialValues: IContactAgentValues;
  poisByIds: Record<PoiId, IBulletin | IProject>;
  navigate: NavigateToFn;
}

const getLastContactListCards = (lastContactedList: DatedPoi[]) => (
  lastContactedList.map(({ date, poiId }) => (
    <React.Fragment key={poiId.id}>
      <ShowAt to={2}>
        <LastContactCard
          poiId={poiId.id}
          date={date}
        />
      </ShowAt>
      <ShowAt from={3}>
        <Col from3={4} from5={3}>
          <LastContactCard
            poiId={poiId.id}
            date={date}
          />
        </Col>
      </ShowAt>
    </React.Fragment>
  ))
);

export const ShortlistPage: React.FC<ShortlistPageProps> = ({
  lastContactedList,
  savedListingList,
  lastViewedIds,
  isLoading,
}) => {
  const [ isCollapsed, setIsCollapsed ] = useState(true);
  const [ savedListingListLocal, setSavedListingListLocal ] = useState(savedListingList);
  const [ isInitialLoading, setIsInitialLoading ] = useState(true);

  const { t } = useLocale();
  const isMobile = useScreenBreakpoint() === 1;

  const savedIds = savedListingList && savedListingList.length ? savedListingList.map(el => el.poiId.id) : [];
  const lastViewedIdsFiltered = lastViewedIds && lastViewedIds.length ? lastViewedIds.filter(el => !savedIds.includes(el)) : [];

  useEffect(() => {
    if (isInitialLoading && !isLoading) setIsInitialLoading(false);
  }, [ isInitialLoading, isLoading ]);

  useEffect(() => {
    setSavedListingListLocal(savedListingList);
  }, [ savedListingList ]);

  if (isInitialLoading) return <ShortlistPageSkeleton />;

  return (
    <ShortlistPageWrapper>
      {!lastContactedList.length && isMobile ? null : (
        <LastContactSectionContainer>
          <Container data-auto="last-contact-container">
            <Row>
              <Col>
                <SubsectionHeader weight="medium">{t('shortlist.lastContact.label')}</SubsectionHeader>
              </Col>
            </Row>

            {lastContactedList.length ? (
              <>
                <ShowAt from={3}>
                  <ShowAt at={5}>
                    {isExtraLargeDesktop => {
                      const rowSize = isExtraLargeDesktop ? 4 : 3;
                      return (
                        <>
                          <Row>
                            {getLastContactListCards(lastContactedList.slice(0, rowSize))}
                            {isCollapsed ? null : getLastContactListCards(lastContactedList.slice(rowSize))}
                          </Row>
                          {lastContactedList.length > rowSize ? (
                            <Row>
                              <Col>
                                <ShowMore onClick={() => setIsCollapsed(!isCollapsed)}>
                                  {isCollapsed ? (
                                    <>{t('collapsable.showMore')}<ChevronDown/></>
                                  ) : (
                                    <>{t('collapsable.showLess')}<ChevronUp/></>
                                  )}
                                </ShowMore>
                              </Col>
                            </Row>
                          ) : null}
                        </>
                      );
                    }}
                  </ShowAt>
                </ShowAt>
                <ShowAt to={2}>
                  <Row>
                    <CardsCarousel>
                      {getLastContactListCards(lastContactedList)}
                    </CardsCarousel>
                  </Row>
                </ShowAt>
              </>
            ) : (
              <Col>
                <LastContactEmptyState>
                  <ChatActiveIcon width={33} height={50} />
                  <ShortlistPageEmptyStateText>
                    {t('shortlist.lastContact.emptyState')}
                  </ShortlistPageEmptyStateText>
                </LastContactEmptyState>
              </Col>
            )}
          </Container>
        </LastContactSectionContainer>
      )}

      <Container>
        <ShortListSectionWrapper data-auto="saved-listings-container" isListCard={isMobile}>
          <ShowAt from={2}>
            <Row>
              <Col>
                <ShortlistHeader data-auto={`savedListing-block-title`} weight="bold">{t('shortlist.savedListing.label')}</ShortlistHeader>
              </Col>
            </Row>
          </ShowAt>
          {savedListingListLocal.length ? (
            <Row>
              {savedListingListLocal.map(({ poiId }) => (
                <Col key={poiId.id} from2={6} from3={4} from5={3} data-auto="saved-listing">
                  <ShortlistCardWithControls
                    id={poiId.id}
                    eventSource={CardEventSource.Favorites}
                    hasControls
                    withPaddingBottom
                    showAddToFavourites
                    noTags
                    isListCard={isMobile}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            <Row>
              <Col from3={6.1}>
                <EmptyStateWrapper>
                  <EmptyState icon={<HeartExtraIcon />} text={t('shortlist.savedListing.emptyState', { isMobile })}/>
                </EmptyStateWrapper>
              </Col>
            </Row>
          )}
        </ShortListSectionWrapper>

        {lastViewedIdsFiltered.length ? (
          <ShortListSectionWrapper>
            <Row>
              <Col>
                <SubsectionHeader weight="medium">{t('shortlist.recentlyView.label')}</SubsectionHeader>
              </Col>
            </Row>
            <Row>
              <ShowAt to={2}>
                <ScrollBox cacheKey="recentlyViewed">
                  {lastViewedIdsFiltered.map(id => (
                    <ShortlistCardWithControls
                      key={id}
                      id={id}
                      withPaddingBottom
                      onlyOneImageShouldBeDisplayed
                      eventSource={CardEventSource.Favorites}
                      showAddToFavourites
                    />
                  ))}
                </ScrollBox>
              </ShowAt>
              <ShowAt from={3}>
                <CardsCarousel hasArrows>
                  {lastViewedIdsFiltered.map(id => (
                    <ShortlistCardWithControls
                      key={id}
                      id={id}
                      withPaddingBottom
                      eventSource={CardEventSource.Favorites}
                      disableSwipe
                      showAddToFavourites
                    />
                  ))}
                </CardsCarousel>
              </ShowAt>
            </Row>
          </ShortListSectionWrapper>
        ) : null}
      </Container>
      <FooterWrapper>
        <Container>
          <SimpleCopyrightFooter />
        </Container>
      </FooterWrapper>
    </ShortlistPageWrapper>
  );
};
